import { Key, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { DropDownProps } from './Dropdown';

export const useDropdown = (props: DropDownProps) => {
  const selectedKeysSignature = getSelectedKeysSignature(props);
  const [selected, setSelected] = useState(() => keysFromSignature(selectedKeysSignature));

  const [signatureInitial, setSignatureInitial] = useState(selectedKeysSignature);
  if (selectedKeysSignature !== signatureInitial) {
    setSignatureInitial(selectedKeysSignature);
    setSelected(keysFromSignature(selectedKeysSignature));
  }

  useEffect(() => {
    setSelected(keysFromSignature(selectedKeysSignature));
  }, [selectedKeysSignature]);

  const buttonRef = useRef<HTMLElement>();
  const [buttonWidth, setButtonWidth] = useState(0);

  useLayoutEffect(() => {
    function updateSize() {
      setButtonWidth(buttonRef?.current?.offsetWidth);
    }

    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const selectedValue = useMemo(
    () =>
      Array.from(selected)
        .map((item) => props.items[item]?.selectedText ?? props.items[item]?.text)
        .join(', '),
    [selected, props.items],
  );

  const selectedDescription = useMemo(
    () =>
      Array.from(selected)
        .map((item) => props.items[item]?.description)
        .join(', '),
    [selected],
  );

  const selectedIcon = useMemo(() => {
    if (props.multiselect) return undefined;

    return props.items[Array.from(selected)[0]]?.icon;
  }, [selected]);

  const selectedRightIcon = useMemo(() => {
    if (props.multiselect) return undefined;

    return props.items[Array.from(selected)[0]]?.rightIcon;
  }, [selected]);

  const disabledKeysLocal = useMemo(() => {
    return props.disabledKeys?.map((key) =>
      props.items.findIndex((item) => (item.key || item.text) === key).toString(),
    );
  }, [props.disabledKeys]);

  return {
    selected,
    setSelected,
    selectedValue,
    selectedDescription,
    selectedIcon,
    buttonWidth,
    buttonRef,
    disabledKeysLocal,
    selectedRightIcon,
  };
};

export const getSelectedKeysSignature = ({ selectedIndex, selectedIndexes }: DropDownProps) => {
  const indexes = selectedIndexes ?? (selectedIndex !== undefined ? [selectedIndex] : []);
  return indexes.map((index) => `${index}`).join(',');
};

const keysFromSignature = (signature: string) => new Set<Key>(signature ? signature.split(',') : []);

export default useDropdown;
